/** @jsx jsx */
import { jsx } from "@emotion/react";
import _debounce from "lodash/debounce";
import { FC, useCallback, useEffect, useState } from "react";
import { ButtonType, FlowioInputType } from "../../../../globalTypes";
import { formatMsg } from "../../../../utils/intl/intlFormmaters";
import EwaButton2 from "../../../atoms/inputs/EwaButtons/EwaButton/EwaButton2";
import FlowioFileDropzone from "../../../atoms/inputs/FlowioFileDropzone";
import FlowioInput from "../../../atoms/inputs/FlowioInput";
import NoFooterModal from "../NoFooterModal";
import { textareaCss } from "./styles";
import { AdditionalTextareaProps, FeedbackModalButtonProps } from "./types";

export const MAX_FILE_SIZE = 5242880;

export type Props = {
  isOpen: boolean;
  toggle: () => void;
  modalTitle: string;
  isSubmitting: boolean;
  onSubmit: () => void;
  message: string;
  files: File[];
  onAddFiles: (files: File[]) => void;
  onFileDelete: (index: number) => void;
  changeMessage: (msg: string) => void;
  buttonProps?: FeedbackModalButtonProps;
  flowioInputProps?: AdditionalTextareaProps;
};

const GeneralFeedbackModal: FC<Props> = ({
  isOpen,
  toggle,
  modalTitle,
  onSubmit,
  isSubmitting,
  message,
  changeMessage,
  files,
  onFileDelete,
  onAddFiles,
  flowioInputProps: { css: flowioInputCss, ...flowioInputProps } = {},
  buttonProps: { css: buttonCss, ...buttonProps } = {},
}) => {
  const [pasteListener, setPasteListener] = useState<HTMLElement | null>(null);
  const [localMessage, setModalMessage] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChangeMsg = useCallback(_debounce(changeMessage, 500), []);

  const onChangeMessage = (newVal: string) => {
    setModalMessage(newVal);
    debouncedChangeMsg(newVal);
  };

  useEffect(() => {
    if (!isOpen) setModalMessage("");
  }, [isOpen]);

  return (
    <NoFooterModal isOpen={isOpen} toggle={toggle} title={modalTitle}>
      <div ref={setPasteListener}>
        <FlowioInput
          type={FlowioInputType.Textarea}
          disabled={isSubmitting}
          value={localMessage}
          onChange={onChangeMessage}
          css={[textareaCss, flowioInputCss]}
          config={{ minLength: 7, maxLength: 500 }}
          {...flowioInputProps}
        />

        <FlowioFileDropzone
          files={files}
          onDelete={onFileDelete}
          onDropAccepted={onAddFiles}
          accept={["image/*", "application/pdf"]}
          acceptedTypesLabel={formatMsg("file_dropzone.image_and_pdf")}
          maxSize={MAX_FILE_SIZE} // 5MB
          pasteListenerTarget={pasteListener}
        />
        <br />
        <EwaButton2
          data-cy="feedback-bug-button"
          css={buttonCss}
          isLoading={isSubmitting}
          disabled={isSubmitting || !message}
          onClick={onSubmit}
          type={ButtonType.Submit}
          autoFocus
          {...buttonProps}
        />
      </div>
    </NoFooterModal>
  );
};

export default GeneralFeedbackModal;
export type GeneralFeedbackModalProps = Props;
